import { spliceItemsIntoCollectionById } from './utils';

import { END_SESSION } from './session';
import { DELETE_RECOGNITION_REQUEST_SUCCESS, POST_RECOGNITION_REQUEST_SUCCESS } from './recognition';

export const RECOGNITIONS_PAGE_LIMIT = 25;

const CLEAR_ERROR = 'RECOGNITIONS::CLEAR_ERROR';
const LIST_RECOGNITIONS_REQUEST_SENT = 'RECOGNITIONS::LIST_RECOGNITIONS_REQUEST_SENT';
const LIST_RECOGNITIONS_REQUEST_SUCCESS = 'RECOGNITIONS::LIST_RECOGNITIONS_REQUEST_SUCCESS';
const LIST_RECOGNITIONS_REQUEST_FAIL = 'RECOGNITIONS::LIST_RECOGNITIONS_REQUEST_FAIL';
const PAGE_RECOGNITIONS_REQUEST_SENT = 'RECOGNITIONS::PAGE_RECOGNITIONS_REQUEST_SENT';
const PAGE_RECOGNITIONS_REQUEST_SUCCESS = 'RECOGNITIONS::PAGE_RECOGNITIONS_REQUEST_SUCCESS';
const PAGE_RECOGNITIONS_REQUEST_FAIL = 'RECOGNITIONS::PAGE_RECOGNITIONS_REQUEST_FAIL';
const UPDATE_RECOGNITIONS_REQUEST_SENT = 'RECOGNITIONS::UPDATE_RECOGNITIONS_REQUEST_SENT';
const UPDATE_RECOGNITIONS_REQUEST_SUCCESS = 'RECOGNITIONS::UPDATE_RECOGNITIONS_REQUEST_SUCCESS';
const UPDATE_RECOGNITIONS_REQUEST_FAIL = 'RECOGNITIONS::UPDATE_RECOGNITIONS_REQUEST_FAIL';

/* These are the actions and reducer that handle the dashboard slice of the global state */
const initialState = {
  error: undefined,
  listRecognitionsRequestOut: false,
  pageRecognitionsRequestOut: false,
  recognitions: [],
  recognitionsLoaded: false,
  updateRecognitionsRequestOut: false,
};

export default (state = initialState, action) => {
  switch (action.type) {
    case CLEAR_ERROR:
      return {
        ...state,
        error: undefined,
      };
    case DELETE_RECOGNITION_REQUEST_SUCCESS:
      return {
        ...state,
        recognitions: [...state.recognitions.filter(recog => recog.id !== action.id)],
      };
    case END_SESSION:
      return { ...initialState };
    case LIST_RECOGNITIONS_REQUEST_SENT:
      return { ...state, listRecognitionsRequestOut: true };
    case LIST_RECOGNITIONS_REQUEST_SUCCESS:
      return {
        ...state,
        recognitions: action.recognitions,
        error: undefined,
        listRecognitionsRequestOut: false,
        recognitionsLoaded: action.recognitionsLoaded,
      };
    case LIST_RECOGNITIONS_REQUEST_FAIL:
      return { ...state, error: action.error, listRecognitionsRequestOut: false };
    case PAGE_RECOGNITIONS_REQUEST_SENT:
      return { ...state, pageRecognitionsRequestOut: true };
    case PAGE_RECOGNITIONS_REQUEST_SUCCESS:
      return {
        ...state,
        recognitions: action.recognitions.concat(state.recognitions),
        recognitionsLoaded: action.recognitionsLoaded,
        error: undefined,
        pageRecognitionsRequestOut: false,
      };
    case PAGE_RECOGNITIONS_REQUEST_FAIL:
      return { ...state, error: action.error, pageRecognitionsRequestOut: false };
    case UPDATE_RECOGNITIONS_REQUEST_SENT:
      return { ...state, updateRecognitionsRequestOut: true };
    case UPDATE_RECOGNITIONS_REQUEST_SUCCESS: {
      let recognitions = spliceItemsIntoCollectionById(
        state.recognitions,
        action.recognitions,
        action.stripOlder,
        action.stripNewer,
      );

      if (action.filterOlder) {
        recognitions = recognitions.filter(item => action.filterOlder <= item.created);
      }

      if (action.clearCollection) {
        recognitions = [];
      }

      return {
        ...state,
        recognitions,
        error: undefined,
        updateRecognitionsRequestOut: false,
      };
    }
    case UPDATE_RECOGNITIONS_REQUEST_FAIL:
      return { ...state, error: action.error, updateRecognitionsRequestOut: false };
    case POST_RECOGNITION_REQUEST_SUCCESS:
      return {
        ...state,
        recognitions: [...state.recognitions.concat([action.recognition])],
      };
    default:
      return state;
  }
};

export const clearError = () => ({
  type: CLEAR_ERROR,
});

export const listRecognitions = config => (dispatch, getState) => {
  const { rmi } = getState();

  dispatch({ type: LIST_RECOGNITIONS_REQUEST_SENT });
  return rmi.recognitions.list({ limit: RECOGNITIONS_PAGE_LIMIT, ...config })
    .then((list) => {
      dispatch({
        type: LIST_RECOGNITIONS_REQUEST_SUCCESS,
        recognitions: list.items,
        recognitionsLoaded: list.items.length !== RECOGNITIONS_PAGE_LIMIT,
      });
    })
    .catch((error) => {
      dispatch({ type: LIST_RECOGNITIONS_REQUEST_FAIL, error });
    });
};

export const pageRecognitions = config => (dispatch, getState) => {
  const { rmi } = getState();
  const { recognitions } = getState().recognitions;
  const oldestRecognition = recognitions[0];

  dispatch({ type: PAGE_RECOGNITIONS_REQUEST_SENT });
  return rmi.recognitions.list({
    before: encodeURIComponent(oldestRecognition.created),
    limit: RECOGNITIONS_PAGE_LIMIT,
    ...config,
  })
    .then((list) => {
      dispatch({
        type: PAGE_RECOGNITIONS_REQUEST_SUCCESS,
        recognitions: list.items,
        recognitionsLoaded: list.items.length !== RECOGNITIONS_PAGE_LIMIT,
      });
    })
    .catch((error) => {
      dispatch({ type: PAGE_RECOGNITIONS_REQUEST_FAIL, error });
    });
};

export const updateRecognitions = config => (dispatch, getState) => {
  const { rmi } = getState();
  const { recognitions } = getState().recognitions;
  const oldestRecognition = recognitions[0];
  let requestCount = 0;

  function sendListRequest(listConfig) {
    return (
      rmi.recognitions.list(listConfig)
        .then((list) => {
          let clearCollection = false;
          let filterOlder = false;
          const items = list.items.filter(item => oldestRecognition.created <= item.created);
          requestCount += 1;
          if (list.items.length === 0) {
            if (requestCount === 1) {
              clearCollection = true;
            } else {
              filterOlder = decodeURIComponent(listConfig.before);
            }
          }

          // if this is the first request, we know that we have the newest recognitions
          // on the database, and can throw away any newer recognitions in memory
          const stripNewer = requestCount === 1;

          // if the list has been filtered, then there were older items, and any
          // unmatched old collection should be discarded
          const stripOlder = list.items.length !== items.length;


          dispatch({
            type: UPDATE_RECOGNITIONS_REQUEST_SUCCESS,
            recognitions: items,
            stripNewer,
            stripOlder,
            clearCollection,
            filterOlder,
          });
          return items;
        })
        .then((items) => {
          const oldestItem = items[0];
          if (oldestRecognition && oldestItem && oldestRecognition.created < oldestItem.created) {
            return sendListRequest({
              ...listConfig,
              before: encodeURIComponent(oldestItem.created),
              limit: RECOGNITIONS_PAGE_LIMIT,
            });
          }
          return items;
        })
    );
  }

  dispatch({ type: UPDATE_RECOGNITIONS_REQUEST_SENT });
  return sendListRequest({ limit: RECOGNITIONS_PAGE_LIMIT, ...config })
    .catch((error) => {
      dispatch({ type: UPDATE_RECOGNITIONS_REQUEST_FAIL, error });
    });
};
